import { axiosPrivate } from "./axios";
import axios from "./axios";
import { authState } from "../hooks/useAuth"; // Import the authState directly
import { pState } from "../context/profileContext";
import { snapshot } from "valtio";

// Function to refresh the token
const refreshToken = async () => {
    try {
        const response = await axios.post(
            '/token/refresh',
            { token: authState.data.refreshToken },
            { headers: { 'Content-Type': 'application/json' } }
        );
        
        // Update the auth state
        authState.data = {
            ...authState.data,
            accessToken: response.data.accessToken
        };
        
        return response.data.accessToken;
    } catch (error) {
        console.error('Token refresh failed:', error);
        // You might want to redirect to login page or handle this error
        // Since we can't use navigate here, you could use window.location
        // or dispatch a custom event that your app can listen for
        window.location.href = '/loggedout';
        return Promise.reject(error);
    }
};

// Set up request interceptor
const requestIntercept = axiosPrivate.interceptors.request.use(
    config => {
        if (!config.headers['Authorization']) {
            const token = authState.data?.accessToken || sessionStorage.getItem('portalToken');
            if (token) config.headers['Authorization'] = `Bearer ${token}`;
        }
        
        // Add clientId from profile to headers
        const profile = snapshot(pState);
        if (profile?.clientId) {
            config.headers['X-Client-Id'] = profile.clientId;
        }
        
        return config;
    }, 
    (error) => Promise.reject(error)
);

// Set up response interceptor
const responseIntercept = axiosPrivate.interceptors.response.use(
    response => response,
    async (error) => {
        const prevRequest = error?.config;
        
        // Handle offline case first
        if (!navigator.onLine) {
            return Promise.reject({
                ...error,
                response: { data: { message: 'No internet connection' } }
            });
        }

        // Handle case where there's no response (e.g., network error)
        if (!error.response) {
            return Promise.reject({
                ...error,
                response: { data: { message: 'Network error' } }
            });
        }

        // Check if refresh token fails
        if (error.response?.config?.url === '/token/refresh') {
            console.log('Token Refresh Failed');
            window.location.href = '/loggedout';
            return Promise.reject(error);
        }

        // Handle 403 with token refresh
        if (error.response?.status === 403 && !prevRequest?.sent) {
            try {
                prevRequest.sent = true;
                const newAccessToken = await refreshToken();
                prevRequest.headers['Authorization'] = `Bearer ${newAccessToken}`;
                return axiosPrivate(prevRequest);
            } catch (refreshError) {
                // If refresh fails, navigate to logged out
                window.location.href = '/loggedout';
                return Promise.reject(refreshError);
            }
        }

        return Promise.reject(error);
    }
);

// Export the configured axios instance
export default axiosPrivate;

// Export utility functions for API calls (similar to useApi)
export const apiService = {
    get: async (resource) => {
        const controller = new AbortController();
        try {
            const url = `api/${resource}`;
            const response = await axiosPrivate.get(url, {
                signal: controller.signal
            });
            
            return response.data;
        } catch (err) {
            controller.abort();
            return (err.response);
        }
    },
    
    getStream: async (entity) => {
        const controller = new AbortController();

        try {
            const url = `api/${entity}`;
            const response = await axiosPrivate.get(url, {
                signal: controller.signal,
                responseType: 'blob',
            });

            if (response.status === 500) {
                return { error: 'File doesn\'t exist anymore'};
            }

            const file = new File([response.data], 'logo.png', { type: response.data.type });
            return file;
        } catch (err) {
            console.log('There is an Error', err);
            controller.abort();
            return err.response;
        }
    },
    
    put: async (entity, data, id) => {
        const controller = new AbortController();

        try {
            let url;
            //omits the id in the url if it is a setting we are retrieving.
            if (entity.toLowerCase().includes('settings')) {
                url = `api/${entity}`;
            } else {
                url = `api/${entity}/${id}`;
            }

            const response = await axiosPrivate.put(url, data, {
                signal: controller.signal
            });
            return response.data;
        } catch (err) {
            controller.abort();
            return (err.response.data);
        }
    },
    
    post: async (entity, data) => {
        const controller = new AbortController();
        try {
            const url = `api/${entity}`;
            const response = await axiosPrivate.post(url, data);
            return response.data;
        } catch (err) {
            controller.abort();
            return (err.response.data);
        }
    },
    
    postStream: async (entity, data) => {
        const controller = new AbortController();

        try {
            const url = `api/${entity}`;
            const response = await axiosPrivate.post(url, data, {
                signal: controller.signal,
                responseType: 'blob',
            });

            const file = new Blob([response.data],
                { type: 'application/pdf' });

            return file;
        } catch (err) {
            controller.abort();
            return (err.response.data);
        }
    },
    
    postForm: async (entity, file, dataItems) => {
        const controller = new AbortController();

        const formData = new FormData();

        //Append the File to formData
        formData.append('file', file);

        // Append each key-value pair from dataItems
        Object.entries(dataItems).forEach(([key, value]) => {
            formData.append(key, value);
        });

        try {
            const url = `api/${entity}`;
            const response = await axiosPrivate.post(url, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
                signal: controller.signal,
            });

            return response;
        } catch (err) {
            controller.abort();
            return (err.response.data);
        }
    },
    
    delete: async (entity, id) => {
        const controller = new AbortController();
        try {
            const url = `api/${entity}/${id}`;
            const response = await axiosPrivate.delete(url);
            return response.data;
        } catch (err) {
            controller.abort();
            return (err.response.data);
        }
    }
};
